import { IndemnityAgreementSignatureMethod, IndemnityStatus } from "@inrev/common";
import { Icon, PartialIndicator } from "@portal/components/primitives";
import { Separator } from "@portal/components/ui/Separator";
import { match } from "ts-pattern";

// We can actually have a synthetic status that is incomplete
type ExtendedIndemnityStatus = IndemnityStatus | "incomplete";

export const IndemnityAgreementStatusIndicator = ({
	status,
	signatureMethod,
	indemnitorsCompleted,
	indemnitorsTotal,
}: {
	status: ExtendedIndemnityStatus;
	signatureMethod: IndemnityAgreementSignatureMethod | null;
	indemnitorsCompleted?: number;
	indemnitorsTotal?: number;
}) => {
	return match(status)
		.with(IndemnityStatus.pending, () => {
			if (signatureMethod === IndemnityAgreementSignatureMethod.physical) {
				return <IndemnityAgreementStatusBase status={IndemnityStatus.pending} />;
			}
			if (signatureMethod === IndemnityAgreementSignatureMethod.electronic) {
				return (
					<IndemnityAgreementStatusBase status={IndemnityStatus.pending}>
						{indemnitorsCompleted !== undefined && indemnitorsTotal !== undefined && (
							<span className="text-[13px] text-tertiary font-medium">
								{indemnitorsCompleted} / {indemnitorsTotal}
							</span>
						)}
					</IndemnityAgreementStatusBase>
				);
			}
			return <IndemnityAgreementStatusBase status={"incomplete"} />;
		})
		.with(IndemnityStatus.review, () => {
			return <IndemnityAgreementStatusBase status={IndemnityStatus.review} />;
		})
		.with(IndemnityStatus.complete, () => {
			return <IndemnityAgreementStatusBase status={IndemnityStatus.complete} />;
		})
		.with("incomplete", () => {
			return <IndemnityAgreementStatusBase status="incomplete" />;
		})
		.exhaustive();
};

const IndemnityAgreementStatusBase = ({
	status,
	children,
}: {
	status: ExtendedIndemnityStatus;
	children?: React.ReactNode;
}) => {
	return (
		<div className="flex items-center space-x-[7px] min-w-fit text-[14px] text-secondary font-medium">
			{IndemnityAgreementStatusIconMap[status]}
			<span>{IndemnityStatusMap[status]}</span>
			{children && (
				<>
					<Separator orientation="vertical" className="h-[20px] bg-bc-default/80" />
					{children}
				</>
			)}
		</div>
	);
};

const IndemnityAgreementStatusIconMap: Record<ExtendedIndemnityStatus, React.ReactNode> = {
	incomplete: <PartialIndicator active className="mt-[2px]" />,
	pending: <Icon type="clock" size="mini" />,
	review: <Icon type="user" size="mini" />,
	complete: <Icon type="check" size="mini" color="confirm" />,
};

const IndemnityStatusMap: Record<ExtendedIndemnityStatus, string> = {
	incomplete: "Incomplete",
	pending: "Pending",
	review: "In Review",
	complete: "Complete",
};
